import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Camera, Loader2 } from 'lucide-react';

import {
    getAttachmentViewUrl,
    getLeadPhotos,
    uploadLeadPhoto,
} from '@/app/services/attachmentService';

interface Props {
    leadId: number;
    siteId?: number | null;
}

/**
 * Shop photos — the thing that actually replaces posting pictures into a WhatsApp
 * group. Backed by the shared /attachments endpoints with entity_type='lead'.
 */
export function LeadPhotoStrip({ leadId, siteId }: Props) {
    const queryClient = useQueryClient();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [preview, setPreview] = useState<string | null>(null);

    const { data: photos } = useQuery({
        queryKey: ['lead-photos', leadId],
        queryFn: () => getLeadPhotos(leadId),
    });

    const uploadMutation = useMutation({
        mutationFn: async (files: File[]) => {
            for (const file of files) {
                await uploadLeadPhoto(leadId, file, siteId);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['lead-photos', leadId] });
            queryClient.invalidateQueries({ queryKey: ['lead', leadId] });
            toast.success('Photo added');
        },
        onError: () => toast.error('Could not upload the photo'),
    });

    const openPhoto = async (attachmentId: number) => {
        try {
            const url = await getAttachmentViewUrl(attachmentId);
            setPreview(url);
        } catch {
            toast.error('Could not open the photo');
        }
    };

    return (
        <div>
            <p className="mobile-section-header mb-2">Photos</p>

            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                className="hidden"
                onChange={(e) => {
                    const files = Array.from(e.target.files ?? []);
                    if (files.length) uploadMutation.mutate(files);
                    e.target.value = '';
                }}
            />

            <div className="flex gap-2 overflow-x-auto pb-1">
                {(photos ?? []).map((photo) => (
                    <button
                        key={photo.id}
                        onClick={() => openPhoto(photo.id)}
                        className="h-20 w-20 flex-shrink-0 rounded-lg border border-border bg-secondary flex items-center justify-center overflow-hidden"
                    >
                        <Camera className="h-6 w-6 text-muted-foreground" />
                    </button>
                ))}

                <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadMutation.isPending}
                    className="h-20 w-20 flex-shrink-0 rounded-lg border border-dashed border-border flex flex-col items-center justify-center gap-1 text-muted-foreground active:bg-accent"
                >
                    {uploadMutation.isPending ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                        <>
                            <Camera className="h-5 w-5" />
                            <span className="text-[10px]">Add</span>
                        </>
                    )}
                </button>
            </div>

            {preview && (
                <div
                    className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
                    onClick={() => setPreview(null)}
                >
                    <img src={preview} alt="" className="max-h-full max-w-full rounded-lg" />
                </div>
            )}
        </div>
    );
}
